// Best-effort clipboard write. Used by the plug inspiration path to copy
// the outlet URL after a successful upload, so the user can paste it
// straight into Slack or a doc without a follow-up step.
//
// Cross-platform: macOS shells out to `pbcopy`, Linux tries `xclip` then
// `xsel`, Windows uses `clip.exe`. All paths handle absence gracefully —
// no crash, no thrown error, no console noise — when the binary is missing
// or the platform is unsupported.
//
// Mirrors the shape of services/os-notify.ts so tests can inject a spawn
// adapter and never touch the real binaries.

export interface CopyToClipboardDeps {
  /** Override platform detection. Defaults to process.platform. */
  platform?: NodeJS.Platform;
  /**
   * Spawn adapter. Receives the binary, its args, and the stdin payload.
   * Resolves true on success, false on any failure.
   */
  spawn?: (cmd: string, args: string[], stdin: string) => Promise<boolean>;
}

/**
 * Default spawn adapter — shells out and writes `stdin` to the child's
 * stdin. Resolves true on exit 0, false on every other outcome.
 */
const defaultSpawn = async (
  cmd: string,
  args: string[],
  stdin: string,
): Promise<boolean> => {
  const { spawn } = await import('node:child_process');
  return new Promise<boolean>((resolve) => {
    let resolved = false;
    let proc: ReturnType<typeof spawn>;
    try {
      proc = spawn(cmd, args, { stdio: ['pipe', 'ignore', 'ignore'] });
    } catch {
      resolve(false);
      return;
    }
    const settle = (ok: boolean) => {
      if (resolved) return;
      resolved = true;
      resolve(ok);
    };
    proc.on('error', () => settle(false));
    proc.on('close', (code) => settle(code === 0));
    try {
      proc.stdin?.end(stdin);
    } catch {
      settle(false);
    }
  });
};

/**
 * Copy `text` to the system clipboard. Resolves false silently when the
 * platform is unsupported or the underlying binary fails. Never throws.
 */
export async function copyToClipboard(
  text: string,
  deps: CopyToClipboardDeps = {},
): Promise<boolean> {
  const platform = deps.platform ?? process.platform;
  const spawn = deps.spawn ?? defaultSpawn;

  const safeSpawn = async (
    cmd: string,
    args: string[],
    stdin: string,
  ): Promise<boolean> => {
    try {
      return await spawn(cmd, args, stdin);
    } catch {
      return false;
    }
  };

  if (platform === 'darwin') {
    return safeSpawn('pbcopy', [], text);
  }

  if (platform === 'linux') {
    if (await safeSpawn('xclip', ['-selection', 'clipboard'], text)) return true;
    return safeSpawn('xsel', ['--clipboard', '--input'], text);
  }

  if (platform === 'win32') {
    return safeSpawn('clip.exe', [], text);
  }

  return false;
}
